import React, { useCallback, useEffect, useRef, useState } from "react";

import { Grid, Typography, Box, IconButton, Stack } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";

import {
    BreadCrumbs,
    Input,
    Select,
    ExitConfirmationModal,
    LoadingBar,
    InputMobileNumber,
    InputEmail
} from "../Common";

import {
    trakrefFontFamily,
    trakrefFontFamilySemiBold,
    trakrefFontFamilyBold,
} from "../../utils";

import useFileUpload from "../../hooks/useFileUpload";
import useFormDirty from "../../hooks/useFormDirty";
import useNavigate from "../../hooks/useNavigate";
import useBreadCrumbs from "../../hooks/useBreadCrumbs";

import { trakrefBlue } from "../../color_utils";
import Routes from "../../routes";
import UserInfo from "../../UserInfo"
import toast from "react-hot-toast";

const initialValues = {
    // General Information
    logo: null,
    instanceId: null,
    name: null,
    shortName: null,
    accountType: null,
    industryTypeId: null,
    energyStarPartnerTypeId: null,
    leakRateCalculationTypeId: null,
    // Billing Address
    street1: null,
    street2: null,
    city: null,
    postalCode: null,
    stateCode: null,
    countryCode: null,
    title: null,
    // Primary Contact
    firstName: null,
    lastName: null,
    mainPhone: null,
    mainPhoneCountryCode: null,
    mainPhoneExt: null,
    alternatePhone: null,
    alternatePhoneCountryCode: null,
    alternatePhoneExt: null,
    email: null,
    fax: null
}

const intialSelectValues = {
    energyStarPartnerTypes: [],
    industryTypes: [],
    leakRateCalculationTypes: [],
    states: [],
    countries: []
}

const formatValue = (val) => {
    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }
    return val;
};


const AccountDetailsForm = () => {
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [accountDetails, setAccountDetaiils] = useState(initialValues);
    const [options, setOptions] = useState(intialSelectValues);
    const [logo, setLogo] = useState(null);
    const [states, setStates] = useState([]);
    const fileInputRef = useRef();

    const navigate = useNavigate({
        pageName:'AccountDetailsForm'
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'AccountDetailsForm', text: 'Edit', link: window.location.pathname }
    });
    const { initializeData, isDirty } = useFormDirty();
    const { readFileDataAsBase64 } = useFileUpload();

    const onHandleFormChange = (name, value) => {

        if (name === "countryCode") {
            const newStates = states.filter(x => x.countryCode === value);
            setOptions({
                ...options,
                states: newStates
            });
            setAccountDetaiils(prev => ({
                ...prev,
                stateCode: null
            }));
        }

        setAccountDetaiils(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const onValidateField = (value) => {
        return value && value.length > 0 ? false : true;
    };

    const onHandleFormSave = async () => {
        try {
            setIsSaving(true);

            if (hasError || onValidateField(accountDetails.name)) {
                setIsSaving(false);
                return;
            }

            const body = {
                ...accountDetails,
                mainPhone: accountDetails.mainPhone && `${accountDetails.mainPhoneCountryCode} ${formatValue(accountDetails.mainPhone)}`,
                alternatePhone: accountDetails.alternatePhone && `${accountDetails.alternatePhoneCountryCode} ${formatValue(accountDetails.alternatePhone)}`,
            }

            const response = await fetch(Routes.Account.UpdateAccountDetails(UserInfo.instanceId), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });

            if (response.status === 200) {
                toast.success("The account details have been successfully updated.", { duration: 6000 });
                navigate.goBack("/AccountDetails");
            }
        }
        catch {
            toast.error("Something went wrong!", { duration: 6000 });
            setIsSaving(false);
        }
        finally {
            setIsSaving(false);
        }
    };

    const onHandleCloseForm = () => {
        if (isDirty(accountDetails)) {
            setShowExitModal(true);
            return;
        }

        navigate.goBack("/AccountDetails");
    };

    const onUploadFileClick = () => {
        fileInputRef.current.click();
    };

    const onFileChange = async (e) => {
        const file = e.target.files[0];
        const byteFile = await readFileDataAsBase64(file);

        const body = {
            fileName: file.name,
            contentType: file.type,
            fileBytes: byteFile
        }

        const response = await fetch(Routes.File.UploadAccountLogo(), {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (response.status === 200) {
            setLogo(URL.createObjectURL(file));
        }
    };

    const fetchAccountDetails = useCallback(async () => {
        try {
            setLoading(true);

            const response = await fetch(Routes.Account.GetAccountDetails(true));
            const data = await response.json();

            if (response.status !== 200) {
                return null;
            }

            const {
                energyStarPartnerTypes: energyStarPartnerTypesOption,
                industryTypes: industryTypesOption,
                leakRateCalculationTypes: leakRateCalculationTypesOption,
                states: stateOption,
                countries: countriesOption
            } = data;

            const energyStarPartnerTypes = energyStarPartnerTypesOption.map(({ id, displayName }) => ({ id, name: displayName }));
            const industryTypes = industryTypesOption.map(({ id, displayName }) => ({ id, name: displayName }));
            const leakRateCalculationTypes = leakRateCalculationTypesOption.map(({ id, longDisplayName }) => ({ id, name: longDisplayName }));
            const states = stateOption.map(({ stateCode, stateName, countryCode }) => ({ id: stateCode, name: stateName, countryCode }));
            const filterdStates = states.filter(x => x.countryCode === data.countryCode);
            const countries = countriesOption.map(({ countryCode, countryName }) => ({ id: countryCode, name: countryName }));

            const newAccountDetails = {
                // General Information
                instanceId: data.instanceId,
                name: data.name,
                shortName: data.shortName,
                accountType: data.accountType,
                industryTypeId: data.industryTypeId,
                energyStarPartnerTypeId: data.energyStarPartnerTypeId,
                leakRateCalculationTypeId: data.leakRateCalculationTypeId,
                // Billing Address
                street1: data.street1 ? data.street1 : "",
                street2: data.street2 ? data.street2 : "",
                city: data.city,
                postalCode: data.postalCode,
                stateCode: data.stateCode,
                countryCode: data.countryCode,
                title: data.title,
                // Primary Contact
                firstName: data.firstName,
                lastName: data.lastName,
                mainPhone: data.mainPhone,
                mainPhoneCountryCode: data.mainPhone && data.mainPhone[0] === "+" ? data.mainPhone.split(" ")[0].trimStart() : "+1",
                mainPhoneExt: data.mainPhoneExt,
                alternatePhone: data.alternatePhone,
                alternatePhoneCountryCode: data.alternatePhone && data.alternatePhone[0] === "+" ? data.alternatePhone.split(" ")[0].trimStart() : "+1",
                alternatePhoneExt: data.alternatePhoneExt,
                email: data.email,
                fax: data.fax
            };

            setLogo(data.logo);
            setStates(states);
            setOptions({
                energyStarPartnerTypes,
                industryTypes,
                leakRateCalculationTypes,
                states: filterdStates,
                countries
            });
            setAccountDetaiils(newAccountDetails);
            initializeData(newAccountDetails);
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
        }
    }, [UserInfo.instanceId]);

    useEffect(() => {
        fetchAccountDetails();
    }, [fetchAccountDetails]);

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: "row",
                    maxWidth: "67%",
                }}
            >
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                    }}
                >
                    <IconButton aria-label="close" onClick={onHandleCloseForm}>
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        variant="h5"
                        sx={{ "&.MuiTypography-root": { fontFamily: trakrefFontFamily } }}
                        style={{ color: "#1A173B" }}
                    >
                        Edit Account Details
                    </Typography>
                </Box>

                <LoadingButton
                    variant="contained"
                    loading={isSaving}
                    onClick={onHandleFormSave}
                    sx={{
                        "& .MuiCircularProgress-root": {
                            color: "#FFFFFF",
                            width: "21px !important",
                            height: "21px !important",
                        },
                        "&.MuiButton-root": {
                            fontFamily: trakrefFontFamilyBold,
                            backgroundColor: trakrefBlue,
                            letterSpacing: "1.2px",
                        },
                    }}
                >
                    <span style={{ color: isSaving ? "transparent" : "#fff" }}>
                        Edit Account Details
                    </span>
                </LoadingButton>
            </Box>

            <Grid
                container
                ml={4}
                sx={{
                    maxWidth: "65%",
                    mt: "-1px",
                    mb: "5rem",
                    "& .MuiGrid-root": {
                        padding: "15px 0px 0px 3.5px",
                    },
                }}
                spacing={2}
            >
                <Grid item xs={12}>
                    <div className="editprofile-header-container">
                        {logo ? (
                            <img className="greenImageLarge" src={logo} />
                        ) : (
                            <div className="greenCircleLarge">{accountDetails.shortName}</div>
                        )}
                        <div>
                            <Typography
                                variant="h5"
                                sx={{
                                    "&.MuiTypography-root": {
                                        fontFamily: trakrefFontFamilySemiBold,
                                        fontSize: "16px",
                                    },
                                }}
                                style={{ color: "#333333" }}
                            >
                                {accountDetails.name}
                            </Typography>
                            <span className="update-image-label" onClick={onUploadFileClick}>
                                {logo ? "Update Logo" : "Upload Logo"}
                            </span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: "none " }}
                                accept="image/png, image/gif, image/jpeg"
                                onChange={onFileChange}
                            />
                        </div>
                    </div>

                    <Grid container spacing={6}>
                        <Grid item xs={6} md={6}>
                            <p className="inputfields-header-label">
                                General Information
                            </p>
                            <Stack spacing={3}>
                                <Input
                                    label="Account Type"
                                    value={accountDetails.accountType}
                                    disabled
                                />
                                <Input
                                    required
                                    label="Name"
                                    value={accountDetails.name}
                                    error={onValidateField(accountDetails.name)}
                                    onChange={(e) => onHandleFormChange("name", e.target.value)}
                                />
                                <Select
                                    label="Industry Type"
                                    options={options.industryTypes}
                                    value={accountDetails.industryTypeId}
                                    onChange={(e) => onHandleFormChange("industryTypeId", e.target.value)}
                                />
                                <Select
                                    label="Energy Star Partner Type"
                                    options={options.energyStarPartnerTypes}
                                    value={accountDetails.energyStarPartnerTypeId}
                                    onChange={(e) => onHandleFormChange("energyStarPartnerTypeId", e.target.value)}
                                />
                                <Select
                                    label="Leak rate calculation type"
                                    options={options.leakRateCalculationTypes}
                                    value={accountDetails.leakRateCalculationTypeId}
                                    onChange={(e) => onHandleFormChange("leakRateCalculationTypeId", e.target.value)}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <p className="inputfields-header-label">
                                Billing Address
                            </p>
                            <Stack spacing={3}>
                                <Input
                                    label="Street 1"
                                    value={accountDetails.street1}
                                    onChange={(e) => onHandleFormChange("street1", e.target.value)}
                                />
                                <Input
                                    label="Street 2"
                                    value={accountDetails.street2}
                                    onChange={(e) => onHandleFormChange("street2", e.target.value)}
                                />
                                <Input
                                    label="City"
                                    value={accountDetails.city}
                                    onChange={(e) => onHandleFormChange("city", e.target.value)}
                                />
                                <Select
                                    label="Country"
                                    options={options.countries}
                                    value={accountDetails.countryCode}
                                    onChange={(e) => onHandleFormChange("countryCode", e.target.value)}
                                />
                                {
                                    options.states.length > 0 ?
                                        <Select
                                            label="State"
                                            options={options.states}
                                            value={accountDetails.stateCode}
                                            onChange={(e) => onHandleFormChange("stateCode", e.target.value)}
                                        /> :
                                        <Input
                                            label="State"
                                            value={accountDetails.stateCode}
                                            onChange={(e) => onHandleFormChange("stateCode", e.target.value)}
                                        />
                                }
                                <Input
                                    type="number"
                                    label="Zip Code"
                                    value={accountDetails.postalCode}
                                    onChange={(e) => onHandleFormChange("postalCode", e.target.value)}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <p className="inputfields-header-label">
                                Primary Contact
                            </p>
                            <Stack spacing={3}>
                                <Input
                                    label="Title"
                                    value={accountDetails.title}
                                    onChange={(e) => onHandleFormChange("title", e.target.value)}
                                />
                                <Input
                                    label="First name"
                                    value={accountDetails.firstName}
                                    onChange={(e) => onHandleFormChange("firstName", e.target.value)}
                                />
                                <Input
                                    label="Last name"
                                    value={accountDetails.lastName}
                                    onChange={(e) => onHandleFormChange("lastName", e.target.value)}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <Stack spacing={3} sx={{ marginTop: "30px" }}>
                                <InputEmail
                                    label="Email"
                                    value={accountDetails.email}
                                    isValid={(e) => setHasError(e)}
                                    onChange={(e) => onHandleFormChange("email", e.target.value)}
                                />
                                <InputMobileNumber
                                    fullWidth
                                    label="Mobile number"
                                    value={accountDetails.mainPhone}
                                    isValid={(e) => setHasError(e)}
                                    onChange={(e) => onHandleFormChange("mainPhone", e.target.value)}
                                    getCountryCode={(value) =>
                                        onHandleFormChange("mainPhoneCountryCode", value)
                                    }
                                />
                                <Input
                                    type="number"
                                    label="Mobile number extension"
                                    value={accountDetails.mainPhoneExt}
                                    onChange={(e) => onHandleFormChange("mainPhoneExt", e.target.value)}
                                />
                                <InputMobileNumber
                                    fullWidth
                                    label="Alternate mobile number"
                                    value={accountDetails.alternatePhone}
                                    isValid={(e) => setHasError(e)}
                                    onChange={(e) => onHandleFormChange("alternatePhone", e.target.value)}
                                    getCountryCode={(value) =>
                                        onHandleFormChange("alternatePhoneCountryCode", value)
                                    }
                                />
                                <Input
                                    type="number"
                                    label="Alternate mobile number extension"
                                    value={accountDetails.alternatePhoneExt}
                                    onChange={(e) => onHandleFormChange("alternatePhoneExt", e.target.value)}
                                />
                                <Input
                                    label="Fax"
                                    value={accountDetails.fax}
                                    onChange={(e) => onHandleFormChange("fax", e.target.value)}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ExitConfirmationModal
                open={showExitModal}
                close={() => setShowExitModal(false)}
                leaveOnClick={() => navigate.goBack("/AccountDetails")}
                stayOnClick={() => setShowExitModal(false)}
                isEditing={!!accountDetails.accountType}
            />
        </>
    )
}

export default AccountDetailsForm;